/**
 * VISTOERA_BILLING_PLAN_CREDITS_TOPUP_PAYG_REWARDS_AND_PROMOTIONS_
 * FOUNDATION_P0_V1 — shared display-only helpers for the Billing Domain
 * admin surfaces (`PlanCreditsBillingPage.tsx` + its tab components).
 *
 * Task spec §19 — "禁止 JS float 直接做最终财务计算": every one of these
 * helpers is a DISPLAY-only convenience (money -> major-unit string for
 * an `InputNumber`, or a Credit/Price ratio for the admin's own quick
 * mental-math preview). The authoritative amount_minor <-> major-unit
 * conversion and the persisted plan/price/credit values always come
 * straight from the backend response — nothing here is ever sent back
 * as an authoritative financial write; every write endpoint takes
 * `amount_minor: number` (an integer) directly.
 */

export const ACTIVE_CURRENCIES = ['CNY', 'USD'] as const

export const CURRENCY_MINOR_EXPONENT: Record<string, number> = {
  CNY: 2,
  USD: 2,
  EUR: 2,
  JPY: 0,
  GBP: 2,
  HKD: 2,
  SGD: 2,
}

export const CURRENCY_SYMBOL: Record<string, string> = {
  CNY: '¥',
  USD: '$',
  EUR: '€',
  JPY: '¥',
  GBP: '£',
  HKD: 'HK$',
  SGD: 'S$',
}

/** Integer minor units -> a plain JS number of major units, for feeding
 * an antd `InputNumber`'s `value` prop only (display/editing UX, never
 * itself sent back to the API — every mutation call site re-derives a
 * fresh `amount_minor` integer from the raw user input). */
export function minorToMajorNumber(amountMinor: number, currency: string): number {
  const exponent = CURRENCY_MINOR_EXPONENT[currency] ?? 2
  return amountMinor / 10 ** exponent
}

/** Major-unit user input (e.g. `200` typed into an `InputNumber` for
 * CNY) -> integer minor units, rounding to the nearest minor unit
 * (never a bare truncation) before it is ever transmitted. */
export function majorNumberToMinor(majorAmount: number, currency: string): number {
  const exponent = CURRENCY_MINOR_EXPONENT[currency] ?? 2
  return Math.round(majorAmount * 10 ** exponent)
}

export function formatMoneyMinor(amountMinor: number | null | undefined, currency: string): string {
  if (amountMinor === null || amountMinor === undefined) return '—'
  const symbol = CURRENCY_SYMBOL[currency] ?? `${currency} `
  return `${symbol}${minorToMajorNumber(amountMinor, currency).toLocaleString('zh-CN', { minimumFractionDigits: 2, maximumFractionDigits: 4 })}`
}

/** Price / Credit unit-economics preview (task spec §6.5 — "¥200 /
 * 12,000 = ¥0.01667 / Credit"), display only. */
export function formatPricePerCredit(amountMinor: number, currency: string, credits: number): string {
  if (!credits || credits <= 0) return '—'
  const major = minorToMajorNumber(amountMinor, currency)
  const symbol = CURRENCY_SYMBOL[currency] ?? `${currency} `
  return `${symbol}${(major / credits).toFixed(4)}`
}

/** Re-export site-wide integer credit display (VistoeraTASK-00745). */
export { formatCredits } from '../../ui/creditsFormat'

export function formatDateTimeShort(iso: string | null | undefined): string {
  if (!iso) return '—'
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return '—'
  return d.toLocaleString('zh-CN', { hour12: false })
}

export function errMsg(err: unknown, fallback: string): string {
  const data = (err as { response?: { data?: { code?: string; detail?: { code?: string; message?: string } } } })
    ?.response?.data
  const code = data?.code || data?.detail?.code
  const detailMessage = data?.detail?.message
  const raw = (err as { message?: string })?.message
  return code ? `${detailMessage || raw || fallback} (${code})` : detailMessage || raw || fallback
}

export const PERIOD_LABEL: Record<string, string> = {
  monthly: 'Monthly / 月',
  yearly: 'Yearly / 年',
  one_time: '一次性',
}

export const EXHAUSTION_POLICY_LABEL: Record<string, string> = {
  STOP: '停止收费功能',
  TOPUP_REQUIRED: '提示用户充值',
  PAYG_ALLOWED: '允许继续按量计费',
}

// Canonical machine values MUST match backend `CreditValidityMode`
// (backend/app/saas/models/plan.py) exactly — this is the UI-facing
// label map, not the source of truth for the machine value itself.
// VISTOERA_BILLING_PLAN_CREDIT_VALIDITY_MODE_SUBSCRIPTION_PERIOD_
// CONTRACT_MISMATCH_FORENSIC_AND_END_TO_END_FIX_P0_V1 — this key was
// previously `SUBSCRIPTION_PERIOD`, which the backend never accepted
// (backend canonical value is `SUBSCRIPTION_CYCLE`), causing a real
// Preview save failure: "Unknown credit_validity_mode
// 'SUBSCRIPTION_PERIOD'". Fixed to align with backend; do not rename
// either side independently again.
export const CREDIT_VALIDITY_LABEL: Record<string, string> = {
  SUBSCRIPTION_CYCLE: '跟随订阅周期',
  FIXED_DAYS: '固定 N 天',
  PERMANENT: '永久',
}
